import { Navigate } from "react-router-dom";
import { legalPages } from "@/data/content";
import PageShell from "./PageShell";
import SiteCTA from "./SiteCTA";
import Reveal from "@/components/base/Reveal";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";

export default function LegalPage({ slug }: { slug: string }) {
  const page = legalPages.find((p) => p.slug === slug);
  const { settings } = useSiteSettingsContext();
  const t = (s: string) => s.replace(/\{siteName\}/g, settings.siteName || "DersDinle");

  if (!page) return <Navigate to="/" replace />;

  const companyName = settings.companyName?.trim() || settings.legalName?.trim() || "";
  const hasCorporate = Boolean(
    companyName || settings.address?.trim() || settings.city?.trim()
  );

  const crumbs = [
    { label: "Ana Sayfa", href: "/" },
    { label: page.title },
  ];

  return (
    <PageShell crumbs={crumbs}>
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20 border-b border-background-200">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground-950 leading-tight">
              {page.title}
            </h1>
            <div className="mt-6 rounded-md border border-warm/40 bg-warm/10 p-4">
              <p className="text-sm text-foreground-800 leading-relaxed">
                Bu sayfa <strong>taslak bilgilendirme metnidir</strong> ve hukuki olarak
                onaylanmış bir metin değildir. Platform yayına hazır hale geldiğinde bu
                metinler ilgili uzmanlar tarafından gözden geçirilip güncellenecektir.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {slug === "kvkk" && hasCorporate && (
        <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-10 md:py-14 border-b border-background-200">
          <div className="max-w-3xl mx-auto">
            <Reveal>
              <div className="rounded-lg border border-background-200 bg-background-100/50 p-6">
                <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                  <i className="ri-building-2-line text-primary-600" aria-hidden="true"></i>
                  Veri Sorumlusu
                </h2>
                <dl className="mt-4 space-y-3 text-sm text-foreground-700">
                  {companyName && (
                    <div className="flex flex-col sm:flex-row sm:gap-2">
                      <dt className="font-semibold text-foreground-900 sm:w-40 shrink-0">Unvan</dt>
                      <dd>{companyName}</dd>
                    </div>
                  )}
                  {settings.address && (
                    <div className="flex flex-col sm:flex-row sm:gap-2">
                      <dt className="font-semibold text-foreground-900 sm:w-40 shrink-0">Adres</dt>
                      <dd>
                        {settings.address}
                        {settings.city && `, ${settings.city}`}
                        {settings.postalCode && ` ${settings.postalCode}`}
                      </dd>
                    </div>
                  )}
                  {settings.contactEmail && (
                    <div className="flex flex-col sm:flex-row sm:gap-2">
                      <dt className="font-semibold text-foreground-900 sm:w-40 shrink-0">İletişim</dt>
                      <dd>
                        <a href={`mailto:${settings.contactEmail}`} className="text-primary-700 hover:text-primary-800">
                          {settings.contactEmail}
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-10">
            {page.sections.map((section) => (
              <Reveal key={section.heading}>
                <article>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p) => (
                    <p
                      key={p}
                      className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed"
                    >
                      {t(p)}
                    </p>
                  ))}
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SiteCTA />
    </PageShell>
  );
}